import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EmptyState } from "../components/EmptyState";
import { EmBreve } from "../components/EmBreve";
import { PublishWizard } from "../components/PublishWizard";
import { ApplicationsPanel } from "../components/company/ApplicationsPanel";
import { AnalyticsPanel } from "../components/company/AnalyticsPanel";
import { AccountSection } from "../components/AccountSection";
import { WalletPacks } from "../components/WalletPacks";
import { ReferralCard } from "../components/ReferralCard";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Briefcase, Users, Eye, Zap, Plus, Crown, Building, FileText, Sparkles, BarChart2, TrendingUp, MousePointerClick, Upload, Image, Image as ImageIcon, Shield } from "lucide-react";
import { JobCoverImage } from "../components/JobCoverImage";
import { JobCoverDialog } from "../components/company/JobCoverDialog";
import { InterviewScheduleDialog } from "../components/company/InterviewScheduleDialog";
import { BoostTierCards } from "../components/company/BoostTierCards";
import { BulkScreeningPanel } from "../components/company/BulkScreeningPanel";

const JOB_STATUS = { pending: ["Em moderação", "bg-amber-100 text-amber-700"], active: ["Ativa", "bg-emerald-100 text-emerald-700"], rejected: ["Rejeitada", "bg-red-100 text-red-700"], closed: ["Fechada", "bg-slate-100 text-slate-600"] };

const SECTOR_OPTIONS = [
  "Alimentação e Bebidas", "Retalho e Distribuição", "Logística e Transportes",
  "Hotelaria e Turismo", "Saúde e Bem-estar", "Construção", "Tecnologia",
  "Serviços ao Consumidor", "Indústria", "Limpeza e Facilities", "Educação", "Financeiro", "Outro",
];
const EMPLOYEE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "+500"];
const BENEFIT_OPTIONS = [
  "Seguro de saúde", "Refeição incluída", "Ginásio incluído", "Trabalho remoto",
  "Horário flexível", "22 dias de férias", "Formação contínua", "Bónus anual",
  "Viatura de empresa", "Subsídio de transporte", "Telemóvel de empresa",
  "Plano de pensões", "Vale refeição", "Seguro de vida", "Plano de ações",
];

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-black/5 p-5">
      <Icon className="w-5 h-5 text-amber-500 mb-3" strokeWidth={1.5} />
      <p className="font-heading font-black tracking-tighter text-3xl text-slate-900">{value}</p>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mt-1">{label}</p>
    </div>
  );
}

export default function CompanyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [coverJob, setCoverJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [meta, setMeta] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [boosting, setBoosting] = useState(null);
  const [matching, setMatching] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [screening, setScreening] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [activeTab, setActiveTab] = useState("jobs");
  const [interviewDialog, setInterviewDialog] = useState(null);
  const [interviewSaving, setInterviewSaving] = useState(false);

  const refresh = useCallback(() => {
    api.get("/company/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/company/jobs").then((r) => setJobs(r.data.jobs)).catch(() => {});
    api.get("/company/applications").then((r) => setApps(r.data.applications)).catch(() => {});
    api.get("/company/wallet").then((r) => setWalletData(r.data)).catch(() => {});
    api.get("/company/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/company/profile").then((r) => {
      const p = r.data;
      setCompanyProfile({
        company_name: p.company_name || "",
        website: p.website || "",
        district: p.district || "",
        about: p.about || "",
        sector: p.sector || "",
        employee_count: p.employee_count || "",
        culture: p.culture || "",
        benefits: p.benefits || [],
        linkedin_url: p.linkedin_url || "",
      });
      if (p.id) {
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${p.id}/logo`);
        setCoverUrl(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${p.id}/cover`);
        setHasLogo(Boolean(p.logo_path));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) navigate("/auth");
    if (user && user.role !== "company") navigate("/");
    if (user && user.role === "company") {
      refresh();
      api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    }
  }, [user, navigate, refresh]);

  if (!user || user.role !== "company") return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /></div>;

  const buyBoost = async (tier, jobId) => {
    setBoosting(jobId + tier);
    try {
      const { data } = await api.post("/payments/checkout", { tier, job_id: jobId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setBoosting(null);
    }
  };

  const updateAppStatus = async (appId, status) => {
    try {
      await api.patch(`/company/applications/${appId}`, { status });
      toast.success("Estado atualizado — o candidato já consegue ver.");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveInterview = async (payload) => {
    if (!interviewDialog) return;
    if (payload?.error) {
      toast.error(payload.error);
      return;
    }
    setInterviewSaving(true);
    try {
      await api.patch(`/company/applications/${interviewDialog.id}`, payload);
      toast.success(payload.interview_scheduled_at === "" ? "Entrevista cancelada" : "Entrevista guardada — o candidato já vê no painel dele.");
      setInterviewDialog(null);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setInterviewSaving(false); }
  };

  const boostWithCredit = async (tier, jobId) => {
    setBoosting(jobId + tier + "credit");
    try {
      await api.post(`/company/jobs/${jobId}/boost-credit`, { tier });
      toast.success("Destaque ativado com 1 crédito da carteira!");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBoosting(null); }
  };

  const bulkScreen = async () => {
    setScreening(true);
    try {
      const { data } = await api.post("/company/applications/screen");
      if (data.processed === 0 && data.skipped === 0) toast.info("Todas as candidaturas já têm match calculado.");
      else toast.success(`Triagem IA: ${data.processed} match(es) calculados${data.skipped ? `, ${data.skipped} sem dados suficientes` : ""}${data.remaining ? `, faltam ${data.remaining}` : ""}.`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setScreening(false); }
  };

  const viewCv = async (appId) => {
    try {
      const res = await api.get(`/applications/${appId}/cv`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Não foi possível abrir o CV"); }
  };

  const computeMatch = async (appId, force = false) => {
    setMatching(appId);
    try {
      const { data } = await api.post(`/company/applications/${appId}/match${force ? "?force=true" : ""}`);
      setApps((prev) => prev.map((a) => a.id === appId ? { ...a, match_score: data.match_score, match_summary: data.match_summary } : a));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setMatching(null); }
  };


  const toggleBenefit = (b) => {
    if (!companyProfile) return;
    const current = companyProfile.benefits || [];
    setCompanyProfile({
      ...companyProfile,
      benefits: current.includes(b) ? current.filter(x => x !== b) : [...current, b],
    });
  };

  const saveCompanyProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/company/profile", companyProfile);
      toast.success("Perfil da empresa atualizado!");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSavingProfile(false); }
  };

  const uploadImage = async (file, type) => {
    if (!file) return;
    const setter = type === "logo" ? setUploadingLogo : setUploadingCover;
    setter(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/company/${type}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (type === "logo") { setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${data.logo_url}?t=${Date.now()}`); setHasLogo(true); }
      else setCoverUrl(`${process.env.REACT_APP_BACKEND_URL}${data.cover_url}?t=${Date.now()}`);
      toast.success(`${type === "logo" ? "Logótipo" : "Imagem de capa"} atualizado!`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setter(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-2">Painel de Empresa</p>
            <h1 data-testid="company-dashboard-title" className="font-heading font-black tracking-tighter text-3xl text-slate-900">{user.name}</h1>
            {stats?.company_status === "pending" && (
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 mt-2">Perfil em validação pela equipa</Badge>
            )}
          </div>
          <Button data-testid="open-wizard-btn" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setWizardOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Publicar vaga
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard testId="stat-active-jobs" icon={Briefcase} label="Vagas ativas" value={stats.active_jobs} />
            <StatCard testId="stat-applications" icon={Users} label="Candidaturas" value={stats.total_applications} />
            <StatCard testId="stat-views" icon={Eye} label="Visualizações" value={stats.total_views} />
            <StatCard testId="stat-boosted" icon={Zap} label="Em destaque" value={stats.boosted_jobs} />
          </div>
        )}

        {companyProfile && (() => {
          const profileComplete = Boolean(
            companyProfile.sector && companyProfile.about && companyProfile.about.length >= 40
            && companyProfile.culture && (companyProfile.benefits || []).length >= 3);
          const approved = stats?.company_status !== "pending";
          const hasJobs = jobs.length > 0;
          const hasBoostOrCredits = jobs.some((j) => j.boost) ||
            ((walletData?.wallet?.express || 0) + (walletData?.wallet?.silver || 0) + (walletData?.wallet?.gold || 0) > 0);
          const items = [
            { id: "account", done: true, title: "Conta de empresa criada",
              description: "Boas-vindas! O consentimento RGPD foi registado." },
            { id: "approved", done: approved,
              title: "Aprovação da empresa",
              description: approved
                ? "A tua empresa está aprovada e visível publicamente."
                : "Estamos a validar a tua empresa. Podes já preencher o perfil enquanto esperas.",
              actionLabel: approved ? null : null },
            { id: "profile", done: profileComplete,
              title: "Completar o perfil (employer branding)",
              description: "Setor, descrição da empresa, cultura e pelo menos 3 benefícios. Ajuda muito o match e a página pública.",
              actionLabel: "Editar perfil", onAction: () => setActiveTab("account") },
            { id: "logo", done: hasLogo,
              title: "Carregar o logótipo",
              description: "O logo aparece na página pública, nos cartões de vaga e no rodapé das candidaturas.",
              actionLabel: "Carregar logo", onAction: () => setActiveTab("account") },
            { id: "first-job", done: hasJobs,
              title: "Publicar a primeira vaga",
              description: "Wizard de 3 passos com IA — palavras-chave → título, descrição e requisitos gerados.",
              actionLabel: "Publicar vaga", onAction: () => setWizardOpen(true) },
            { id: "boost", done: hasBoostOrCredits,
              title: "Ativar um destaque ou comprar pack",
              description: "EXPRESS 15€ (5d), SILVER 45€ (10d), GOLD 99€ (30d). Packs oferecem até -40%. Vagas em destaque recebem ~3× mais visualizações.",
              actionLabel: "Ver destaques", onAction: () => setActiveTab("boosts") },
          ];
          return (
            <OnboardingChecklist
              testId="company-onboarding"
              title="Próximos passos"
              subtitle="Completa estes passos para tirares o máximo partido do First2Job."
              items={items}
            />
          );
        })()}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex flex-wrap h-auto">
            <TabsTrigger data-testid="tab-company-jobs" value="jobs">Vagas</TabsTrigger>
            <TabsTrigger data-testid="tab-company-apps" value="apps">Candidaturas</TabsTrigger>
            <TabsTrigger data-testid="tab-company-analytics" value="analytics"><BarChart2 className="w-4 h-4 mr-1.5" />Analytics</TabsTrigger>
            <TabsTrigger data-testid="tab-company-boosts" value="boosts">Destaques</TabsTrigger>
            <TabsTrigger data-testid="tab-company-screening" value="screening">
              <Sparkles className="w-4 h-4 mr-1.5 text-fuchsia-500" />Triagem IA
            </TabsTrigger>
            <TabsTrigger data-testid="tab-company-account" value="account">Conta</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <EmptyState icon={Briefcase} title="Ainda sem vagas" description="Publica a tua primeira vaga com o wizard passo a passo."
                actionLabel="Publicar vaga" onAction={() => setWizardOpen(true)} />
            ) : (
              <div className="space-y-3">
                {jobs.map((j) => {
                  const [label, cls] = JOB_STATUS[j.status] || JOB_STATUS.closed;
                  const boosted = j.boost && new Date(j.boost.expires_at) > new Date();
                  return (
                    <div key={j.id} data-testid={`company-job-${j.id}`} className="bg-white rounded-xl border border-black/5 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <button type="button" data-testid={`job-cover-thumb-${j.id}`}
                            onClick={() => setCoverJob(j)}
                            className="relative w-24 h-16 rounded-lg overflow-hidden bg-slate-100 shrink-0 border border-black/5 hover:border-amber-400 transition-colors group">
                            <JobCoverImage job={j} className="w-full h-full object-cover" />
                            <span className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 flex items-center justify-center transition-colors">
                              <ImageIcon className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </span>
                          </button>
                          <div className="min-w-0">
                            <p className="font-heading font-bold tracking-tighter text-slate-900 truncate">{j.title}</p>
                            <p className="text-sm text-slate-500">{j.concelho}, {j.district} · {j.views} visualizações · {j.applications_count} candidaturas</p>
                            <button type="button" data-testid={`job-cover-btn-${j.id}`} onClick={() => setCoverJob(j)}
                              className="mt-1 text-xs font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1">
                              <ImageIcon className="w-3.5 h-3.5" /> Alterar imagem da vaga
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {boosted && (
                            <Badge className={
                              j.boost.tier === "gold" || j.boost.tier === "destaque_max" ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 border-0"
                              : j.boost.tier === "silver" || j.boost.tier === "destaque" ? "bg-gradient-to-r from-sky-500 to-cyan-500 text-white border-0"
                              : "bg-gradient-to-r from-orange-500 to-red-500 text-white border-0"
                            }>
                              {j.boost.tier === "gold" || j.boost.tier === "destaque_max" ? <Crown className="w-3 h-3 mr-1" />
                               : j.boost.tier === "express" ? <Zap className="w-3 h-3 mr-1" />
                               : <Shield className="w-3 h-3 mr-1" />}
                              {j.boost.label}
                            </Badge>
                          )}
                          <Badge className={`${cls} hover:${cls}`}>{label}</Badge>
                        </div>
                      </div>
                      {j.status === "active" && !boosted && (
                        <div className="mt-4 pt-4 border-t border-black/5">
                          <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3 text-fuchsia-500" /> Destacar esta vaga
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button data-testid={`boost-express-btn-${j.id}`} size="sm"
                                    className="rounded-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-md ring-1 ring-orange-400"
                                    disabled={boosting === j.id + "express"} onClick={() => buyBoost("express", j.id)}>
                              <Zap className="w-3.5 h-3.5 mr-1.5" /> EXPRESS — 15€ / 5 dias
                            </Button>
                            <Button data-testid={`boost-silver-btn-${j.id}`} size="sm"
                                    className="rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white shadow-md ring-1 ring-sky-400"
                                    disabled={boosting === j.id + "silver"} onClick={() => buyBoost("silver", j.id)}>
                              <Shield className="w-3.5 h-3.5 mr-1.5" /> SILVER — 45€ / 10 dias
                            </Button>
                            <Button data-testid={`boost-gold-btn-${j.id}`} size="sm"
                                    className="rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-950 font-black shadow-md ring-1 ring-amber-300"
                                    disabled={boosting === j.id + "gold"} onClick={() => buyBoost("gold", j.id)}>
                              <Crown className="w-3.5 h-3.5 mr-1.5" /> GOLD — 99€ / 30 dias
                            </Button>
                          </div>
                          {(walletData?.wallet?.express || walletData?.wallet?.silver || walletData?.wallet?.gold) > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {(walletData?.wallet?.express || 0) > 0 && (
                                <Button data-testid={`credit-express-btn-${j.id}`} size="sm" variant="outline"
                                        className="rounded-full border-orange-400 text-orange-700 hover:bg-orange-50"
                                        disabled={boosting === j.id + "expresscredit"} onClick={() => boostWithCredit("express", j.id)}>
                                  <Zap className="w-3 h-3 mr-1.5" /> Usar EXPRESS ({walletData.wallet.express})
                                </Button>
                              )}
                              {(walletData?.wallet?.silver || 0) > 0 && (
                                <Button data-testid={`credit-silver-btn-${j.id}`} size="sm" variant="outline"
                                        className="rounded-full border-sky-400 text-sky-700 hover:bg-sky-50"
                                        disabled={boosting === j.id + "silvercredit"} onClick={() => boostWithCredit("silver", j.id)}>
                                  <Shield className="w-3 h-3 mr-1.5" /> Usar SILVER ({walletData.wallet.silver})
                                </Button>
                              )}
                              {(walletData?.wallet?.gold || 0) > 0 && (
                                <Button data-testid={`credit-gold-btn-${j.id}`} size="sm" variant="outline"
                                        className="rounded-full border-amber-400 text-amber-700 hover:bg-amber-50"
                                        disabled={boosting === j.id + "goldcredit"} onClick={() => boostWithCredit("gold", j.id)}>
                                  <Crown className="w-3 h-3 mr-1.5" /> Usar GOLD ({walletData.wallet.gold})
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="apps">
            <ApplicationsPanel
              apps={apps}
              screening={screening}
              matching={matching}
              onBulkScreen={bulkScreen}
              onComputeMatch={computeMatch}
              onViewCv={viewCv}
              onStatusChange={updateAppStatus}
              onScheduleInterview={setInterviewDialog}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsPanel analytics={analytics} />
          </TabsContent>

          <TabsContent value="boosts">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-black/5 p-6 sm:p-8 mb-8">
              <div className="text-center mb-8 max-w-2xl mx-auto">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-fuchsia-600 mb-2">
                  Escolhe o teu destaque
                </p>
                <h2 className="font-heading font-black tracking-tighter text-3xl text-slate-900">
                  Vagas em destaque recebem até <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-fuchsia-500">3× mais</span> candidaturas
                </h2>
                <p className="text-sm text-slate-500 mt-3">
                  Três níveis pensados para cada momento — do urgente ao prestígio máximo. Pagamento seguro via Stripe.
                </p>
              </div>
              <BoostTierCards
                tiers={meta?.boost_tiers}
                onBuy={null}
                credits={walletData?.wallet}
                onUseCredit={null}
              />
              <p className="text-xs text-slate-400 mt-6 text-center">
                Para ativar um destaque numa vaga, vai ao separador <b>Vagas</b> e escolhe o tier — abre o Stripe Checkout automaticamente.
              </p>
            </div>
            <WalletPacks
              wallet={walletData?.wallet}
              packs={walletData?.packs}
              onGoToScreening={() => setActiveTab("screening")}
            />
            <div className="mt-8 max-w-2xl">
              <ReferralCard role="company" />
            </div>
          </TabsContent>

          <TabsContent value="screening">
            <BulkScreeningPanel jobs={jobs} />
          </TabsContent>

          <TabsContent value="account">
            <div className="max-w-2xl space-y-6">
              {/* Company Profile Form */}
              {companyProfile && (
                <form onSubmit={saveCompanyProfile} className="bg-white rounded-2xl border border-black/5 p-6 sm:p-8 space-y-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-1">Perfil da empresa</p>
                    <h2 className="font-heading font-bold tracking-tighter text-xl text-slate-900">Informações e employer branding</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Visível na página pública da empresa. Melhora a tua taxa de candidaturas com um perfil completo.</p>
                  </div>

                  {/* Logo + Cover Upload */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Logo */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center gap-1"><Image className="w-3.5 h-3.5" /> Logótipo</p>
                      <label className="flex flex-col items-center gap-2 p-4 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-amber-400 transition-colors">
                        {logoUrl ? (
                          <img src={logoUrl} alt="Logo" className="h-16 object-contain rounded-lg" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-white border border-black/5 flex items-center justify-center">
                            <Image className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Upload className="w-3 h-3" /> {uploadingLogo ? "A carregar..." : "Clica para alterar"}
                        </span>
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                          disabled={uploadingLogo}
                          onChange={(e) => uploadImage(e.target.files?.[0], "logo")} />
                      </label>
                      <p className="text-[11px] text-slate-400 mt-1">PNG/JPG/WEBP, máx. 2 MB</p>
                    </div>
                    {/* Cover */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center gap-1"><Image className="w-3.5 h-3.5" /> Imagem de capa</p>
                      <label className="flex flex-col items-center gap-2 p-4 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-amber-400 transition-colors">
                        {coverUrl ? (
                          <img src={coverUrl} alt="Cover" className="h-16 w-full object-cover rounded-lg" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <div className="w-full h-16 rounded-xl bg-white border border-black/5 flex items-center justify-center">
                            <Image className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Upload className="w-3 h-3" /> {uploadingCover ? "A carregar..." : "Clica para alterar"}
                        </span>
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                          disabled={uploadingCover}
                          onChange={(e) => uploadImage(e.target.files?.[0], "cover")} />
                      </label>
                      <p className="text-[11px] text-slate-400 mt-1">PNG/JPG/WEBP, máx. 5 MB, 16:9 ideal</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-1.5 block">Nome da empresa</Label>
                      <Input data-testid="company-name-input" value={companyProfile.company_name}
                        onChange={(e) => setCompanyProfile({ ...companyProfile, company_name: e.target.value })} />
                    </div>
                    <div>
                      <Label className="mb-1.5 block">Website</Label>
                      <Input value={companyProfile.website}
                        onChange={(e) => setCompanyProfile({ ...companyProfile, website: e.target.value })}
                        placeholder="https://www.empresa.pt" type="url" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-1.5 block">Setor de atividade</Label>
                      <Select value={companyProfile.sector || ""} onValueChange={(v) => setCompanyProfile({ ...companyProfile, sector: v })}>
                        <SelectTrigger data-testid="company-sector-select" className="bg-white">
                          <SelectValue placeholder="Escolhe o setor" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTOR_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1.5 block">Nº de colaboradores</Label>
                      <Select value={companyProfile.employee_count || ""} onValueChange={(v) => setCompanyProfile({ ...companyProfile, employee_count: v })}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Dimensão da empresa" /></SelectTrigger>
                        <SelectContent>
                          {EMPLOYEE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o} colaboradores</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-1.5 block">LinkedIn da empresa (URL)</Label>
                    <Input value={companyProfile.linkedin_url}
                      onChange={(e) => setCompanyProfile({ ...companyProfile, linkedin_url: e.target.value })}
                      placeholder="https://www.linkedin.com/company/..." type="url" />
                  </div>

                  <div>
                    <Label className="mb-1.5 block">Sobre a empresa</Label>
                    <Textarea rows={3} value={companyProfile.about}
                      onChange={(e) => setCompanyProfile({ ...companyProfile, about: e.target.value })}
                      placeholder="Breve descrição da empresa, missão e valores..." />
                  </div>

                  <div>
                    <Label className="mb-1.5 block">Cultura & valores</Label>
                    <Textarea rows={4} value={companyProfile.culture}
                      onChange={(e) => setCompanyProfile({ ...companyProfile, culture: e.target.value })}
                      placeholder="Descreve o ambiente de trabalho, estilo de gestão, valores da equipa..." />
                    <p className="text-[11px] text-slate-400 mt-1">Visível na página pública. Candidatos valorizam empresas transparentes.</p>
                  </div>

                  <div>
                    <Label className="mb-2 block">Benefícios</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2.5 gap-x-3">
                      {BENEFIT_OPTIONS.map((b) => (
                        <label key={b} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                          <Checkbox
                            checked={(companyProfile.benefits || []).includes(b)}
                            onCheckedChange={() => toggleBenefit(b)}
                          />
                          {b}
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button data-testid="save-company-profile-btn" type="submit"
                    className="rounded-full bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
                    disabled={savingProfile}>
                    {savingProfile ? "A guardar..." : "Guardar perfil"}
                  </Button>
                </form>
              )}
              <AccountSection />
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <PublishWizard open={wizardOpen} onOpenChange={setWizardOpen} meta={meta} onPublished={refresh} />
      <JobCoverDialog job={coverJob} open={!!coverJob} onOpenChange={(v) => !v && setCoverJob(null)} onSaved={refresh} />
      <InterviewScheduleDialog
        application={interviewDialog}
        onClose={() => setInterviewDialog(null)}
        onSave={saveInterview}
        saving={interviewSaving}
      />
      <Footer />
    </div>
  );
}

